// Chart components for the /vtas Dashboard. Extracted from Dashboard.jsx.
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardContent } from '../ui/card';
import { COLORS, CustomTooltip, CountTooltip, formatMoney, formatMonthLabel } from './utils';

// ===== Monthly evolution bar chart =====
export const MonthlyEvolutionCard = ({ title, monthly, accent = '#F4A261', currency = 'ARS', subtitle }) => {
  const data = (monthly || []).map((m) => ({ ...m, label: formatMonthLabel(m.month) }));
  const totalValor = data.reduce((s, m) => s + (m.valor || 0), 0);
  const totalCobro = data.reduce((s, m) => s + (m.cobro || 0), 0);
  return (
    <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
      <CardContent className="p-5">
        <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
          {title}
        </h3>
        {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}

        {data.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg p-3" style={{ backgroundColor: accent + '15', border: `1px solid ${accent}40` }}>
              <p className="text-[10px] uppercase tracking-wider text-gray-400">Total Facturado</p>
              <p className="text-3xl font-bold leading-tight" style={{ color: accent, fontFamily: "'Bebas Neue', sans-serif" }}>
                {formatMoney(totalValor, currency)}
              </p>
            </div>
            <div className="rounded-lg p-3" style={{ backgroundColor: '#1B6B3A15', border: '1px solid #1B6B3A40' }}>
              <p className="text-[10px] uppercase tracking-wider text-gray-400">Total Cobrado</p>
              <p className="text-3xl font-bold leading-tight" style={{ color: '#1B6B3A', fontFamily: "'Bebas Neue', sans-serif" }}>
                {formatMoney(totalCobro, currency)}
              </p>
            </div>
            <div className="rounded-lg p-3" style={{ backgroundColor: '#E6394615', border: '1px solid #E6394640' }}>
              <p className="text-[10px] uppercase tracking-wider text-gray-400">Pendiente de Cobro</p>
              <p className="text-3xl font-bold leading-tight" style={{ color: '#E63946', fontFamily: "'Bebas Neue', sans-serif" }}>
                {formatMoney(Math.max(totalValor - totalCobro, 0), currency)}
              </p>
            </div>
          </div>
        )}

        {data.length === 0 ? (
          <p className="text-gray-500 text-sm py-12 text-center">Sin datos</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="label" tick={{ fill: '#999', fontSize: 11 }} />
              <YAxis tickFormatter={(v) => formatMoney(v, currency)} tick={{ fill: '#999', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip currency={currency} />} cursor={{ fill: '#ffffff10' }} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Bar dataKey="valor" name="Valor" fill={accent} radius={[4, 4, 0, 0]} />
              <Bar dataKey="cobro" name="Cobrado" fill="#1B6B3A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

// ===== Combined campaigns COUNT chart (AR + INTL stacked by count) =====
export const CombinedCampaignsCard = ({ monthly }) => {
  const data = (monthly || []).map((m) => ({ label: formatMonthLabel(m.month), AR: m.campanas_ar || 0, INTL: m.campanas_intl || 0 }));
  const hasData = data.some((d) => d.AR > 0 || d.INTL > 0);
  return (
    <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
      <CardContent className="p-5">
        <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
          Cantidad de Campañas — Total (AR + INTL)
        </h3>
        <p className="text-xs text-gray-500 mb-3">Cantidad (no facturación, las monedas son distintas)</p>
        {!hasData ? (
          <p className="text-gray-500 text-sm py-12 text-center">Sin datos</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="label" tick={{ fill: '#999', fontSize: 11 }} />
              <YAxis tick={{ fill: '#999', fontSize: 11 }} />
              <Tooltip content={<CountTooltip />} cursor={{ fill: '#ffffff10' }} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Bar dataKey="AR" stackId="c" name="Argentina" fill="#F4A261" />
              <Bar dataKey="INTL" stackId="c" name="Internacional" fill="#FF6EC7" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export const StackedMonthlyChart = ({ monthlyDict, all_months, currency = 'ARS' }) => {
  if (!monthlyDict || Object.keys(monthlyDict).length === 0 || !all_months || all_months.length === 0) {
    return <p className="text-gray-500 text-sm py-12 text-center">Sin datos</p>;
  }
  const names = Object.keys(monthlyDict).slice(0, 10);
  const data = all_months.map((m) => {
    const row = { month: formatMonthLabel(m) };
    names.forEach((name) => {
      const md = monthlyDict[name]?.find((x) => x.month === m);
      row[name] = md ? md.valor : 0;
    });
    return row;
  });
  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#222" />
        <XAxis dataKey="month" tick={{ fill: '#999', fontSize: 12 }} />
        <YAxis tickFormatter={(v) => formatMoney(v, currency)} tick={{ fill: '#999', fontSize: 11 }} />
        <Tooltip content={<CustomTooltip currency={currency} />} cursor={{ fill: '#ffffff10' }} />
        <Legend wrapperStyle={{ fontSize: '11px' }} />
        {names.map((name, i) => (
          <Bar key={name} dataKey={name} stackId="a" fill={COLORS[i % COLORS.length]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export const Trailing12Card = ({ title, subtitle, accent, data, currency, mode }) => {
  // mode: 'valor' | 'campanas' | 'ticket'
  const dataKey = mode;
  const isMoney = mode !== 'campanas';
  const yFmt = isMoney ? ((v) => formatMoney(v, currency)) : ((v) => v.toLocaleString('es-AR'));
  const tooltipContent = isMoney
    ? <CustomTooltip currency={currency} />
    : <CountTooltip />;
  const total = data.reduce((s, d) => s + (d[dataKey] || 0), 0);
  const totalLabel = mode === 'campanas'
    ? `Total: ${total.toLocaleString('es-AR')} campañas`
    : mode === 'ticket'
      ? `Promedio: ${formatMoney(data.filter((d) => d.ticket > 0).reduce((s, d, _, arr) => s + d.ticket / arr.length, 0), currency)}`
      : `Total: ${formatMoney(total, currency)}`;
  return (
    <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
      <CardContent className="p-5">
        <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
          {title}
        </h3>
        {subtitle && <p className="text-xs text-gray-500 mb-2">{subtitle}</p>}
        <p className="text-xs font-semibold mb-3" style={{ color: accent }}>{totalLabel}</p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
            <XAxis dataKey="label" tick={{ fill: '#999', fontSize: 11 }} />
            <YAxis tickFormatter={yFmt} tick={{ fill: '#999', fontSize: 11 }} />
            <Tooltip content={tooltipContent} cursor={{ fill: '#ffffff10' }} />
            <Bar dataKey={dataKey} name={mode === 'campanas' ? 'Campañas' : mode === 'ticket' ? 'Ticket promedio' : 'Facturación'} fill={accent} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export const YoYComparisonCard = ({ title, subtitle, accent, yearA, yearB, dataA, dataB, currency }) => {
  const buildMap = (monthlyArr) => {
    const m = {};
    (monthlyArr || []).forEach((row) => {
      const mo = parseInt(row.month.split('-')[1]);
      if (!isNaN(mo)) m[mo] = row.valor || 0;
    });
    return m;
  };
  const aMap = buildMap(dataA);
  const bMap = buildMap(dataB);
  const monthLabels = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const data = monthLabels.map((label, i) => ({
    month: label,
    [yearA]: aMap[i + 1] || 0,
    [yearB]: bMap[i + 1] || 0,
  }));
  const hasData = data.some((d) => d[yearA] > 0 || d[yearB] > 0);
  return (
    <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
      <CardContent className="p-5">
        <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
          {title}
        </h3>
        {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
        {!hasData ? (
          <p className="text-gray-500 text-sm py-12 text-center">Sin datos comparativos</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="month" tick={{ fill: '#999', fontSize: 11 }} />
              <YAxis tickFormatter={(v) => formatMoney(v, currency)} tick={{ fill: '#999', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip currency={currency} />} cursor={{ fill: '#ffffff10' }} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Bar dataKey={yearA} fill="#9B59B6" radius={[4, 4, 0, 0]} />
              <Bar dataKey={yearB} fill={accent} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};
